/**
 * Engage Service
 * API service for fetching Engage Objects (Video, Blog, Education, etc.)
 */
import { BizEngageType, EngageObject } from '../Types/engage';
import { get } from './common';

const engageEndpoint = 'api/engage';

// Map the raw API response to our EngageObject type
const mapEngageObject = (item: EngageObject): EngageObject => ({
	Id: item.Id,
	Name: item.Name,
	Description: item.Description,
	EngageObjectType: item.EngageObjectType,
	IsActive: item.IsActive,
	EstimatedTimeInMinutes: item.EstimatedTimeInMinutes,
	ImageUrl: item.ImageUrl,
	UrlRewrite: item.UrlRewrite,
});

const engageArrayMapper = (items: EngageObject[]): EngageObject[] => {
	return items.map(mapEngageObject);
};

/**
 * Get engage objects by type for admin selection
 * @param objectType The BizEngageType to filter by
 * @param deepload Whether to include related entities (default false for pickers)
 */
export async function getEngageObjects(objectType: BizEngageType, deepload: boolean = false): Promise<EngageObject[]> {
	const endpoint = `${engageEndpoint}/GetEngageObjects?objectType=${objectType}&deepload=${deepload}`;
	const result = await get<EngageObject[]>(endpoint);
	return result ? engageArrayMapper(result) : [];
}

/**
 * Get all videos (MediabankFile type engage objects)
 */
export async function getVideos(): Promise<EngageObject[]> {
	return getEngageObjects(BizEngageType.Video);
}

/**
 * Get all blog posts
 */
export async function getBlogs(): Promise<EngageObject[]> {
	return getEngageObjects(BizEngageType.Blog);
}

/**
 * Get all online educations (HTML5)
 */
export async function getEducations(): Promise<EngageObject[]> {
	return getEngageObjects(BizEngageType.Education);
}

export function getEngageObjectTypeIdByName(itemName: string): number | undefined {
	switch (itemName) {
		case 'HTML5':
			return BizEngageType.Education;
		case 'Blog':
			return BizEngageType.Blog;
		case 'MediabankFile':
			return BizEngageType.Video;
		case 'SCORM':
			return BizEngageType.SCORM;
		case 'Speedquiz':
			return BizEngageType.Speedquiz;
		case 'Certification':
			return BizEngageType.Certification;
		case 'Mission':
			return BizEngageType.Mission;
		case 'Event':
			return BizEngageType.Event;
		default:
			return undefined;
	}
}

export function getEngageObjectTypeIcon(objectType: number): string {
	switch (objectType) {
		case BizEngageType.Education:
			return 'fa-solid fa-graduation-cap';
		case BizEngageType.Blog:
			return 'fa-regular fa-newspaper';
		case BizEngageType.Video:
			return 'fa-regular fa-circle-play';
		case BizEngageType.SCORM:
			return 'fa-solid fa-graduation-cap';
		case BizEngageType.Speedquiz:
			return 'fas fa-gamepad';
		case BizEngageType.Certification:
			return 'far fa-file-certificate';
		case BizEngageType.Mission:
			return 'fas fa-gamepad';
		case BizEngageType.Event:
			return 'fas fa-calendar';
		default:
			return 'fa-solid fa-circle';
	}
}
